import React, { useContext, useMemo, useState } from "react";
import { Trash2, Pencil } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import ReactSelector from "../../components/common/CommonReactSelector";
import ActionIconButton from "../../components/common/ActionIconButton";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import {
  COMPARE_CONDITIONS,
  COMPARE_CONDITION_EQUAL,
  COMPARE_CONDITION_NOT_EQUAL,
  compareConditionSymbol,
  getCompareTargetFields,
  getCompareValidations,
} from "../../utils/workflowCompareValidation";

// Condition -> Translations.js key, so the condition labels come from the same
// dictionary as the rest of the modal instead of a second lookup table.
const CONDITION_LABEL_KEYS = {
  [COMPARE_CONDITION_EQUAL]: "compareValidationEqual",
  [COMPARE_CONDITION_NOT_EQUAL]: "compareValidationNotEqual",
};

const emptyRule = () => ({
  secondaryUniqueKey: "",
  condition: COMPARE_CONDITION_EQUAL,
  errorMessage: "",
});

const CompareValidationModal = ({
  onClose,
  preview,
  droppedItems,
  onSaveCompareValidation,
  onDeleteCompareValidation,
}) => {
  const { theme, langMode } = useContext(AppContext);
  const t = (key) => translations[langMode]?.[key] ?? translations.en[key];
  const conditionLabel = (condition) => t(CONDITION_LABEL_KEYS[condition]);

  // A rule is identified by the field it points at, so "editing" means loading
  // an existing rule back into the draft and saving over it.
  const [draft, setDraft] = useState(emptyRule);
  const [editingKey, setEditingKey] = useState(null);

  const previewKey = preview?.uniqueKey;
  const targetFields = useMemo(
    () => getCompareTargetFields(droppedItems, previewKey),
    [droppedItems, previewKey],
  );
  const savedRules = getCompareValidations(droppedItems, previewKey);

  const fieldNameFor = (uniqueKey) =>
    targetFields.find((field) => field.uniqueKey === uniqueKey)?.fieldName ??
    t("compareValidationDeletedField");

  const fieldOptions = targetFields.map((field) => ({
    value: field.uniqueKey,
    label: field.fieldName || field.uniqueKey,
  }));
  const conditionOptions = COMPARE_CONDITIONS.map((condition) => ({
    value: condition.value,
    label: `${conditionLabel(condition.value)} (${condition.symbol})`,
  }));

  const isEditing = editingKey !== null;

  const resetDraft = () => {
    setDraft(emptyRule());
    setEditingKey(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!draft.secondaryUniqueKey) {
      toast.warn(t("compareValidationSelectFieldFirst"));
      return;
    }

    const duplicate = savedRules.some(
      (rule) =>
        rule.secondaryUniqueKey === draft.secondaryUniqueKey &&
        rule.secondaryUniqueKey !== editingKey,
    );
    if (duplicate) {
      toast.warn(t("compareValidationDuplicate"));
      return;
    }

    onSaveCompareValidation(previewKey, {
      secondaryUniqueKey: draft.secondaryUniqueKey,
      condition: draft.condition,
      errorMessage: draft.errorMessage.trim(),
    }, editingKey);
    resetDraft();
    toast.success(t("compareValidationSaved"));
  };

  const handleDelete = (rule) => {
    onDeleteCompareValidation(previewKey, rule.secondaryUniqueKey);
    if (editingKey === rule.secondaryUniqueKey) resetDraft();
    toast.success(t("compareValidationRemoved"));
  };

  const handleEdit = (rule) => {
    setDraft({ ...rule });
    setEditingKey(rule.secondaryUniqueKey);
  };

  const inputClass = `w-full rounded-lg border border-slate-200 bg-white p-2.5 text-[13px] text-slate-800 outline-none
    focus:border-[#236DB4] focus:ring-2 focus:ring-[#EAF3FB] ${
      theme === "dark"
        ? "dark-mode "
        : theme === "high-contrast"
          ? "high-contrast"
          : ""
    }`;
  const labelClass = "mb-1 block text-[11px] font-semibold text-slate-600";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        dir={isRTL(langMode) ? "rtl" : "ltr"}
        className={`relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-md ${
          theme === "dark"
            ? "dark-mode border"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
        }`}
      >
        <ModalCloseButton
          className={`absolute top-2 ${isRTL(langMode) ? "left-2" : "right-2"}`}
          onClick={onClose}
        />

        <h2 className="text-md font-semibold">{t("compareValidationTitle")}</h2>
        <p className="mb-4 mt-0.5 text-[11.5px] leading-[1.45] text-slate-500">
          {t("compareValidationSubtitle")}
        </p>

        {fieldOptions.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center text-[12.5px] text-slate-500">
            {t("compareValidationNoFields")}{" "}
            {t("compareValidationEligibleTypes")}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className={labelClass}>{t("compareValidationFirstField")}</label>
              <input
                type="text"
                value={preview?.fieldName || ""}
                readOnly
                disabled
                className={`${inputClass} cursor-not-allowed bg-slate-50 text-slate-500`}
              />
            </div>

            <div>
              <label className={labelClass}>{t("compareValidationCondition")}</label>
              <div className="text-xs">
                <ReactSelector
                  options={conditionOptions}
                  value={conditionOptions.find(
                    (option) => option.value === draft.condition,
                  )}
                  onChange={(selected) =>
                    setDraft((prev) => ({
                      ...prev,
                      condition: selected?.value ?? COMPARE_CONDITION_EQUAL,
                    }))
                  }
                  height="38px"
                  borderRadius="8px"
                  fontSize="13px"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>{t("compareValidationSecondField")}</label>
              <div className="text-xs">
                <ReactSelector
                  options={fieldOptions}
                  value={
                    fieldOptions.find(
                      (option) => option.value === draft.secondaryUniqueKey,
                    ) ?? null
                  }
                  onChange={(selected) =>
                    setDraft((prev) => ({
                      ...prev,
                      secondaryUniqueKey: selected?.value ?? "",
                    }))
                  }
                  placeholder={t("compareValidationSelectSecondField")}
                  isClearable
                  height="38px"
                  borderRadius="8px"
                  fontSize="13px"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="compareErrorMessage">
                {t("compareValidationErrorMessage")}
              </label>
              <input
                id="compareErrorMessage"
                type="text"
                value={draft.errorMessage}
                placeholder={t("compareValidationErrorMessagePlaceholder")}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    errorMessage: event.target.value,
                  }))
                }
                className={inputClass}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <CommonButton colorIntent="danger"
                type="button"
                onClick={isEditing ? resetDraft : onClose}
                variant="danger"
                tooltipText={translations[langMode].closewithoutsavingchnages}
                tooltipPosition="top"
              >
                {t("compareValidationCancel")}
              </CommonButton>
              <CommonButton colorIntent="success" type="submit" variant="success" tooltipPosition="left">
                {isEditing ? t("compareValidationUpdate") : t("compareValidationAdd")}
              </CommonButton>
            </div>
          </form>
        )}

        <div className="mt-5 border-t border-slate-100 pt-4">
          <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">
            {t("compareValidationAdded")}
          </h3>
          {savedRules.length === 0 ? (
            <p className="text-[12.5px] text-slate-500">{t("compareValidationEmpty")}</p>
          ) : (
            <div className="space-y-2">
              {savedRules.map((rule) => (
                <div
                  key={rule.secondaryUniqueKey}
                  className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-700">
                      {preview?.fieldName} {compareConditionSymbol(rule.condition)}{" "}
                      {fieldNameFor(rule.secondaryUniqueKey)}
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-slate-500">
                      {conditionLabel(rule.condition)}
                      {rule.errorMessage ? ` — ${rule.errorMessage}` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <ActionIconButton
                      onClick={() => handleEdit(rule)}
                      tone="blue"
                      ariaLabel={t("compareValidationUpdate")}
                      tooltipText={t("compareValidationUpdate")}
                      tooltipPosition="left"
                    >
                      <Pencil size={15} />
                    </ActionIconButton>
                    <ActionIconButton
                      onClick={() => handleDelete(rule)}
                      tone="red"
                      ariaLabel={translations[langMode].deletevalidation}
                      tooltipText={translations[langMode].deletevalidation}
                      tooltipPosition="left"
                    >
                      <Trash2 size={15} />
                    </ActionIconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareValidationModal;
